/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

import { Heading, Loader } from '@livechat/design-system-react-components';

import Config from 'Config';
import { SignupFormData } from 'types/form';
import { StandardError } from 'types/error';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import Location from 'services/Location';
import { isMobile } from 'helpers/device';
import { anyToBoolean } from 'helpers/boolean';
import { delayPromiseResult } from 'helpers/timeout';
import { fetchProducts } from 'hooks/use-products';
import { Window } from 'types/window';
import Clearbit from 'services/Clearbit';
import { organizationIDEscaped } from 'helpers/account';
import { saveItem } from 'helpers/storage/local-storage';
import SignupTracking from 'services/SignupTracking';
import { SelectTrial } from 'routes/select-trial/SelectTrial';
import { useSharedStore } from 'stores/shared-store';
import { NestedIData } from 'stores/base-store';
import { organizationsClient } from 'services/connectivity/organizations/client';
import { createNameFromEmail } from 'helpers/email';
import { LocalStorageKey } from 'constants/local-storage';

import { SignupInfo } from './components/SignupInfo';
import { SignupError } from './components/SignupError';

import * as styles from './styles';

declare const window: Window;

type Organization = {
  email: string;
  name: string;
  password: string;
  organization: {
    tracking: NestedIData;
    region?: string;
    company: {
      phone: string;
    };
  };
};

export const Signup: FC = () => {
  const [autoSubmit, setAutoSubmit] = useState<boolean | null>(null);
  const [isAutoSubmitPending, setIsAutoSubmitPending] = useState<boolean>(false);
  const [formData, setFormData] = useState<SignupFormData>({
    name: undefined,
    email: undefined,
    password: undefined,
    new_email: undefined,
    coupon_code: undefined,
    country: undefined,
    partner_id: undefined,
    enable_data_enrichment: undefined,
  });
  const [skipSelectTrial, setSkipSelectTrial] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  const [isSignupPhoneLimitException, setIsSignupPhoneLimitException] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<StandardError | null>(null);

  const navigate = useNavigate();
  const signupStore = useSharedStore(['email']);

  const region = Location.getUrlParam('region') || '';
  const cookieProduct = Cookies.get('product');

  useEffect(() => {
    const params = signupStore.toObject();
    const { autoSubmitFromParams, ...formDataFromParams } = params;

    setAutoSubmit(anyToBoolean(autoSubmitFromParams) || false);
    setFormData(formDataFromParams as any);
    checkIfShouldSkipSelectTrial();

    window.LC_API = window.LC_API || {};

    if (isMobile()) {
      window.LC_API.on_before_load = window.LC_API.hide_chat_window;

      if (window.LC_API.is_loaded && window.LC_API.is_loaded()) {
        window.LC_API.hide_chat_window();
      }
    }

    return () => {
      window.LC_API.on_before_load = null;

      if (
        window.LC_API.is_loaded &&
        window.LC_API.is_loaded() &&
        window.LC_API.chat_window_hidden()
      ) {
        try {
          window.LC_API.minimize_chat_window();
        } catch (_) {
          //
        }
      }
    };
  }, []);

  useEffect(() => {
    if (autoSubmit && !isAutoSubmitPending) {
      handleAutoSubmit();
    }
  }, [autoSubmit]);

  const checkIfShouldSkipSelectTrial = async () => {
    setIsLoading(true);
    const clientId = Location.getUrlParam('client_id') || Location.getHashParam('client_id');

    if (clientId) {
      saveItem(LocalStorageKey.ClientId, clientId);
    }

    const refHostname = Location.getReferrerHostname();
    try {
      const products = await fetchProducts();
      const isKnownClientId =
        products.some((product) => product.client_id === clientId) ||
        clientId === Config.default_client_id ||
        clientId === import.meta.env.VITE_APP_CLIENT_ID;
      if (!isKnownClientId && refHostname !== 'text.com') {
        setSkipSelectTrial(true);
        setIsLoading(false);
      }
    } catch {
      if (refHostname !== 'text.com') {
        setSkipSelectTrial(true);
        setIsLoading(false);
      }
    }
    setIsLoading(false);
  };

  const handleAutoSubmit = async () => {
    setIsAutoSubmitPending(true);
    handleSubmit();
    navigate('/signup/done');
  };

  const handleFormDataChange = async (newFormData: Partial<SignupFormData>): Promise<any> =>
    new Promise<void>((resolve) => {
      setFormData((prevData) => {
        const updatedFormData = { ...prevData, ...newFormData };
        signupStore.set(updatedFormData as any);
        resolve();

        return updatedFormData;
      });
    });

  const handleSubmit = (e?: Event) => {
    if (e) {
      e.preventDefault();
    }

    saveItem('signup', '1');
    createLicense();
  };

  const createLicense = async () => {
    setIsCreatingAccount(true);
    formData.name = formData.name || createNameFromEmail(formData.email);

    try {
      const clearbitReveal = await Clearbit.handleRevealData();

      const tracking = signupStore.forAllProducts();

      // Add clearbit details for tracking
      if (tracking && Object.keys(tracking).length && clearbitReveal) {
        Object.keys(tracking).forEach((productTracking) => {
          tracking[productTracking].clearbit_reveal = JSON.stringify(clearbitReveal);
        });
      }

      const organizationParams: Organization = {
        email: formData.email,
        name: formData.name,
        password: formData.password,
        organization: {
          tracking,
          company: {
            phone: formData.phone,
          },
        },
      };

      if (Config.regionIsValid(region)) {
        organizationParams.organization.region = region;
      }

      if (window.clearbit) {
        window.clearbit.identify(formData.email, {
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
        });
      }

      const { result, error } = await organizationsClient.create(organizationParams);

      if (error) {
        throw error;
      }

      await Promise.race([SignupTracking.handleSignUp(), delayPromiseResult(3, false)]);

      window.location = `${Config.accounts_api_url}/?organization_id=${organizationIDEscaped(
        result.organization.organization_id
      )}&access_token=${result.access_token.access_token}&${buildOauthQuery(Config)}`;
    } catch (e) {
      if (e.status === 409) {
        window.location.href = `${window.location.href.replace('/signup', '')}&email=${encodeURIComponent(
          formData.email
        )}&new_signin=1`;

        return;
      }

      if (e.status === 429 && e.error_description === 'too many organizations with phone') {
        setIsSignupPhoneLimitException(true);
        setIsCreatingAccount(false);

        return;
      }

      setIsCreatingAccount(false);

      setRequestError({
        responseCode: e.status || 500,
        requestId: e.request_id || '',
        error: e.error || `An error has occurred. Details: ${e.toString()}`,
        description: e.error_description || '',
      });
    }
  };

  if (isLoading || isCreatingAccount) {
    return (
      <div css={styles.signupLoaderStyles}>
        {isCreatingAccount && <Heading size="lg">Creating your account…</Heading>}
        <Loader size="large" />
      </div>
    );
  }

  if (requestError) {
    return <SignupError error={requestError} />;
  }

  if (!skipSelectTrial && !cookieProduct) {
    return <SelectTrial />;
  }
  return (
    <SignupInfo
      formData={formData}
      onFormDataChange={handleFormDataChange}
      onSubmit={handleSubmit}
      isSignupPhoneLimitException={isSignupPhoneLimitException}
      trackingForAllProducts={signupStore.forAllProducts}
    />
  );
};
